import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, ShieldAlert, ShieldX, ShieldQuestion, Save, Wrench, DollarSign, ExternalLink } from "lucide-react";

const STATUS_STYLE = {
  current: { icon: BadgeCheck, cls: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10", label: "Current" },
  expiring: { icon: ShieldAlert, cls: "border-amber-500/40 text-amber-300 bg-amber-500/10", label: "Expiring soon" },
  expired: { icon: ShieldX, cls: "border-rose-500/40 text-rose-300 bg-rose-500/10", label: "Expired" },
  missing: { icon: ShieldQuestion, cls: "border-zinc-500/40 text-zinc-300 bg-zinc-500/10", label: "Missing" },
};

function daysUntil(iso) {
  if (!iso) return null;
  return Math.round((new Date(iso).getTime() - Date.now()) / (86400 * 1000));
}
function statusFor(iso, signedAt) {
  if (signedAt !== undefined) return signedAt ? "current" : "missing";
  const d = daysUntil(iso);
  if (d === null) return "missing";
  if (d < 0) return "expired";
  if (d <= 30) return "expiring";
  return "current";
}
function StatusPill({ st, days }) {
  const s = STATUS_STYLE[st] || STATUS_STYLE.missing;
  const Icon = s.icon;
  return (
    <Badge variant="outline" className={s.cls}>
      <Icon className="h-3 w-3 mr-1" /> {s.label}
      {days !== null && days !== undefined && st !== "current" && st !== "missing" && (
        <span className="ml-1 font-mono text-[10px]">{days < 0 ? `${Math.abs(days)}d ago` : `${days}d`}</span>
      )}
    </Badge>
  );
}

export default function VendorMyCompliancePage() {
  const [me, setMe] = useState(null);
  const [saving, setSaving] = useState(false);
  const [section, setSection] = useState("coi");
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/vendor/me");
      setMe(data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.expires_at) payload.expires_at = new Date(payload.expires_at).toISOString();
      if (payload.signed_at) payload.signed_at = new Date(payload.signed_at).toISOString();
      if (payload.coverage_usd) payload.coverage_usd = parseFloat(payload.coverage_usd);
      await api.put("/vendor/my/compliance", { section, payload });
      toast.success("Compliance updated — thank you");
      setForm({});
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setSaving(false);
  };

  if (!me) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  const comp = me.compliance || {};
  const coiSt = statusFor((comp.coi || {}).expires_at);
  const w9St = statusFor(null, (comp.w9 || {}).signed_at);
  const licSt = statusFor((comp.license || {}).expires_at);

  return (
    <div>
      <PageHeader eyebrow={me.vendor?.name || "Vendor"} title="My Compliance" subtitle="Keep your COI, W-9, and license up to date to stay eligible for work orders." />

      <div className="grid grid-cols-3 gap-3 mb-4">
        <Card className="border-white/10 bg-card p-4">
          <div className="overline text-[10px]">Certificate of Insurance</div>
          <div className="mt-2"><StatusPill st={coiSt} days={daysUntil((comp.coi || {}).expires_at)} /></div>
          <div className="text-[11px] text-zinc-500 mt-2">
            Carrier: {(comp.coi || {}).carrier || "—"} · Policy: {(comp.coi || {}).policy_no || "—"}
            <br />Expires: {(comp.coi || {}).expires_at ? new Date((comp.coi).expires_at).toLocaleDateString() : "—"}
          </div>
        </Card>
        <Card className="border-white/10 bg-card p-4">
          <div className="overline text-[10px]">W-9</div>
          <div className="mt-2"><StatusPill st={w9St} /></div>
          <div className="text-[11px] text-zinc-500 mt-2">
            EIN last-4: {(comp.w9 || {}).ein_last4 || "—"}
            <br />Signed: {(comp.w9 || {}).signed_at ? new Date((comp.w9).signed_at).toLocaleDateString() : "—"}
          </div>
        </Card>
        <Card className="border-white/10 bg-card p-4">
          <div className="overline text-[10px]">License</div>
          <div className="mt-2"><StatusPill st={licSt} days={daysUntil((comp.license || {}).expires_at)} /></div>
          <div className="text-[11px] text-zinc-500 mt-2">
            #: {(comp.license || {}).number || "—"} · State: {(comp.license || {}).state || "—"}
            <br />Expires: {(comp.license || {}).expires_at ? new Date((comp.license).expires_at).toLocaleDateString() : "—"}
          </div>
        </Card>
      </div>

      <Card className="border-white/10 bg-card p-5">
        <div className="text-xs text-zinc-500 mb-3">Update a section</div>
        <div className="flex gap-2 mb-4">
          {[["coi", "COI"], ["w9", "W-9"], ["license", "License"]].map(([k, label]) => (
            <button key={k} onClick={() => { setSection(k); setForm({}); }}
              data-testid={`my-section-${k}`}
              className={`text-xs px-3 py-1.5 rounded-full border ${section === k ? "border-orange-500/40 text-orange-300 bg-orange-500/10" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}>{label}</button>
          ))}
        </div>
        {section === "coi" && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Carrier</Label><Input data-testid="my-coi-carrier" value={form.carrier || ""} onChange={(e) => setForm({ ...form, carrier: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div><Label>Policy #</Label><Input value={form.policy_no || ""} onChange={(e) => setForm({ ...form, policy_no: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div><Label>Coverage $</Label><Input type="number" value={form.coverage_usd || ""} onChange={(e) => setForm({ ...form, coverage_usd: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div><Label>Expires on</Label><Input data-testid="my-coi-expires" type="date" value={form.expires_at || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
          </div>
        )}
        {section === "w9" && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>EIN last 4</Label><Input data-testid="my-w9-ein" maxLength={4} value={form.ein_last4 || ""} onChange={(e) => setForm({ ...form, ein_last4: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div><Label>Signed on</Label><Input type="date" value={form.signed_at || ""} onChange={(e) => setForm({ ...form, signed_at: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
          </div>
        )}
        {section === "license" && (
          <div className="grid grid-cols-3 gap-3">
            <div><Label>License #</Label><Input value={form.number || ""} onChange={(e) => setForm({ ...form, number: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div><Label>State</Label><Input value={form.state || ""} maxLength={2} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
            <div><Label>Expires on</Label><Input type="date" value={form.expires_at || ""} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className="mt-1 bg-white/[0.04] border-white/10" /></div>
          </div>
        )}
        <Button data-testid="my-save" onClick={save} disabled={saving} className="mt-4 bg-orange-500 hover:bg-orange-600 rounded-full">
          <Save className="h-4 w-4 mr-1" /> {saving ? "Saving…" : "Save changes"}
        </Button>
      </Card>

      {/* Tickets & Bids — the "command centre" bit: from a magic-link visit
          the vendor sees at a glance what work is live, without hunting
          through the Tickets or RFP tabs. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-6">
        <Card className="border-white/10 bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-orange-400" /><div className="font-medium">Active work orders</div></div>
            <Badge variant="outline" className="border-white/10 text-[10px]" data-testid="tickets-count">{me.tickets?.length || 0}</Badge>
          </div>
          <div className="space-y-2">
            {(me.tickets || []).slice(0, 8).map((t) => (
              <Link key={t.id} to={`/vendor/tickets/${t.id}`} data-testid={`ticket-${t.id}`}
                    className="flex items-center justify-between rounded border border-white/10 bg-white/[0.02] p-3 hover:bg-white/[0.04] transition">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{t.title || t.summary || "Untitled ticket"}</div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    {t.property_name || t.property_id} · {(t.priority || "normal")}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant="outline" className="border-white/10 text-[10px] capitalize">{t.status}</Badge>
                  <ExternalLink className="h-3 w-3 text-zinc-500" />
                </div>
              </Link>
            ))}
            {(me.tickets || []).length === 0 && (
              <div className="text-xs text-muted-foreground py-4 text-center">No active work orders right now.</div>
            )}
          </div>
        </Card>

        <Card className="border-white/10 bg-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2"><DollarSign className="h-4 w-4 text-orange-400" /><div className="font-medium">My bids</div></div>
            <Badge variant="outline" className="border-white/10 text-[10px]" data-testid="bids-count">{me.bids?.length || 0}</Badge>
          </div>
          <div className="space-y-2">
            {(me.bids || []).slice(0, 8).map((b) => (
              <Link key={b.id} to={`/vendor/tickets/${b.ticket_id}`} data-testid={`bid-${b.id}`}
                    className="flex items-center justify-between rounded border border-white/10 bg-white/[0.02] p-3 hover:bg-white/[0.04] transition">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">${(b.amount || 0).toLocaleString()} — {(b.notes || "").slice(0, 60) || "no notes"}</div>
                  <div className="text-[10px] text-zinc-500 mt-1">
                    Ticket {(b.ticket_id || "").slice(0, 8)}… · submitted {b.created_at ? new Date(b.created_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <Badge variant="outline" className={
                    b.status === "won" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                    : b.status === "lost" ? "border-rose-500/40 text-rose-300 bg-rose-500/10"
                    : "border-amber-500/40 text-amber-300 bg-amber-500/10"
                  }>{b.status || "pending"}</Badge>
                  <ExternalLink className="h-3 w-3 text-zinc-500" />
                </div>
              </Link>
            ))}
            {(me.bids || []).length === 0 && (
              <div className="text-xs text-muted-foreground py-4 text-center">No bids submitted yet.</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
